import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Activity, Wallet } from "lucide-react";
import { toast } from "sonner";
import { ACTIVITY_KIND_META } from "@/components/investments/shared";

export function ActivityPanel({ position, onClose, onChange }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    kind: "dividend", date: new Date().toISOString().slice(0, 10),
    amount: "", quantity: "", fee: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const symbol = position?.symbol || "";
  const investmentId = position?.id;

  useEffect(() => {
    if (!investmentId) return;
    (async () => {
      try {
        const { data } = await api.get(`/investments/${investmentId}/activities`);
        setRows(data);
      } catch { toast.error("Couldn't load activity"); }
    })();
    // Reset the add-form when opening for a new position
    setForm((f) => ({ ...f, amount: "", quantity: "", fee: "", notes: "" }));
  }, [investmentId]);

  const needsQty = form.kind === "buy" || form.kind === "sell";
  const meta = ACTIVITY_KIND_META[form.kind];
  const Icon = meta?.icon || Activity;

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    if (needsQty && (!form.quantity || Number(form.quantity) <= 0)) {
      toast.error(`${meta.label} requires quantity`); return;
    }
    setSaving(true);
    try {
      const body = {
        kind: form.kind,
        date: form.date,
        amount: Number(form.amount),
        quantity: needsQty ? Number(form.quantity) : null,
        fee: form.fee ? Number(form.fee) : 0,
        notes: form.notes || "",
      };
      const { data } = await api.post(`/investments/${investmentId}/activities`, body);
      setRows((r) => [data, ...r]);
      setForm((f) => ({ ...f, amount: "", quantity: "", fee: "", notes: "" }));
      const realized = data.realized_gain;
      toast.success(
        realized === null || realized === undefined
          ? `${meta.label} logged`
          : `Sold — realized ${realized >= 0 ? "gain" : "loss"} of ${formatCurrency(Math.abs(realized), position.currency)}`
      );
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/investments/${investmentId}/activities/${confirmDel.id}`);
      setRows((r) => r.filter((x) => x.id !== confirmDel.id));
      setConfirmDel(null);
      toast.success("Activity removed");
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <>
      <Dialog open={!!position} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-w-3xl" data-testid="activity-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-[var(--brand)]" />
              Activity — {symbol}
            </DialogTitle>
            <DialogDescription>
              Log dividends, interest, trades, or fees for this position. Every entry auto-posts a matching cash transaction so it shows in your P&amp;L and cash flow.
            </DialogDescription>
          </DialogHeader>

          {/* Add-activity form */}
          <div className="card-flat p-4">
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2">Log new activity</div>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
              <div className="sm:col-span-4">
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="h-9 mt-1" data-testid="activity-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACTIVITY_KIND_META).map(([k, m]) => (
                      <SelectItem key={k} value={k} data-testid={`activity-kind-${k}`}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Date</Label>
                <Input type="date" data-testid="activity-date"
                       value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                       className="h-9 mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                  {form.kind === "buy" ? "Cost" : form.kind === "sell" ? "Proceeds" : "Amount"}
                </Label>
                <Input type="number" step="0.01" min="0" data-testid="activity-amount"
                       value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       className="h-9 mt-1" placeholder="0.00" />
              </div>
              {needsQty && (
                <div className="sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Quantity</Label>
                  <Input type="number" step="0.0001" min="0" data-testid="activity-quantity"
                         value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                         className="h-9 mt-1" placeholder="e.g. 10" />
                </div>
              )}
              {(form.kind === "buy" || form.kind === "sell") && (
                <div className="sm:col-span-1">
                  <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Fee</Label>
                  <Input type="number" step="0.01" min="0" data-testid="activity-fee"
                         value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })}
                         className="h-9 mt-1" placeholder="0" />
                </div>
              )}
              <div className="sm:col-span-12">
                <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Notes</Label>
                <Input data-testid="activity-notes"
                       value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                       placeholder={`e.g. Q1 dividend from ${symbol}`}
                       className="h-9 mt-1" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-3">
              <div className="text-[11px] text-[var(--muted-text)] flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5" />
                Auto-posts a matching {meta?.income ? "income" : "expense"} transaction to your books.
              </div>
              <Button className="btn-brand h-9" data-testid="activity-save" onClick={save} disabled={saving}>
                <Icon className="w-4 h-4 mr-1.5" /> {saving ? "Posting…" : `Log ${meta?.label || "activity"}`}
              </Button>
            </div>
          </div>

          {/* Existing activity list */}
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2">
              History ({rows.length})
            </div>
            {rows.length === 0 ? (
              <div className="text-sm text-[var(--muted-text)] text-center py-6">
                No activity yet — post a dividend or trade to see it here.
              </div>
            ) : (
              <div className="card-flat overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Realized</th>
                      <th className="px-3 py-2">Notes</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a) => {
                      const m = ACTIVITY_KIND_META[a.kind] || ACTIVITY_KIND_META.dividend;
                      const IcRow = m.icon;
                      return (
                        <tr key={a.id} className="border-b border-[var(--divider)] last:border-none"
                            data-testid={`activity-row-${a.id}`}>
                          <td className="px-3 py-2 whitespace-nowrap">{a.date}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${m.tone}`}>
                              <IcRow className="w-3 h-3" /> {m.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{formatCurrency(a.amount, position.currency)}</td>
                          <td className="px-3 py-2 text-right font-mono text-[var(--muted-text)]">{a.quantity ?? "—"}</td>
                          <td className={`px-3 py-2 text-right font-mono ${
                            a.realized_gain == null ? "text-[var(--muted-text)]"
                            : a.realized_gain >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"
                          }`}>
                            {a.realized_gain == null ? "—" : formatCurrency(a.realized_gain, position.currency)}
                          </td>
                          <td className="px-3 py-2 text-xs text-[var(--muted-text)] truncate max-w-[180px]">{a.notes || ""}</td>
                          <td className="px-3 py-2 text-right">
                            <Button size="icon" variant="ghost" data-testid={`activity-del-${a.id}`}
                                    onClick={() => setConfirmDel(a)}>
                              <Trash2 className="w-4 h-4 text-[#E63946]" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this activity?</AlertDialogTitle>
            <AlertDialogDescription>
              The matching cash transaction in your books will also be removed. Buy/Sell entries will reverse their position adjustments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="activity-del-confirm" onClick={remove}
                               className="bg-[#E63946] hover:bg-[#c92e3b] text-white">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}



// =============================================================
// Year-end Tax Report — 1099-B style summary of realized sells
// plus dividend/interest income totals. Users pick a year and
// export CSV. Cost basis is average-cost; long-term = held >365d.
// =============================================================
